import { useIsOnline } from '@/app/store/navigationSlice';
import { useChatRequest } from '@/features/chats/viewmodels/useChatRequest';
import { sendSeenStatus } from '@/infrastructure/sdk';
import { MESSAGE_SEEN_STATUS_ID } from '@/shared/constants';
import { useAppActivity } from '@/shared/hooks';
import { formatUserIdToJid, getLocalUserId, getMessageSenderId, getUserIdFromJid } from '@/shared/utils';
import { useEffect, useRef } from 'react';
import { isGroupChat } from '../utils';

const SEEN_ELIGIBLE_TYPES = new Set([
   'text',
   'auto_text',
   'image',
   'video',
   'location',
   'file',
   'audio',
   'contact',
   'meet',
   'audio_recorded',
]);

let pendingSeenQueue = [];
let flushTimer = null;

const flushSeenQueue = () => {
   if (pendingSeenQueue.length === 0) return;

   const seen = new Map();
   for (const entry of pendingSeenQueue) {
      if (!seen.has(entry.msgId)) seen.set(entry.msgId, entry);
   }

   const sorted = [...seen.values()].sort((a, b) => a.publishedAt - b.publishedAt);

   console.log(
      `[useSendSeen] Flushing ${sorted.length} seen status(es) oldest → newest:`,
      sorted.map(e => e.msgId),
   );

   for (const { toJid, msgId, groupId } of sorted) {
      sendSeenStatus(toJid, msgId, groupId);
   }

   pendingSeenQueue = [];
   flushTimer = null;
};

const DEBOUNCE_MS = 80;

const enqueueSeenStatus = entry => {
   pendingSeenQueue.push(entry);
   clearTimeout(flushTimer);
   flushTimer = setTimeout(flushSeenQueue, DEBOUNCE_MS);
};

// ─── Shared core logic ───────────────────────────────────────────────────────

const processSeen = ({ message, activeChatJid }) => {
   // Grouped media: unwrap and process each child individually
   if (message.type === 'groupedMedia' && Array.isArray(message.messages)) {
      for (const child of message.messages) {
         processSeen({ message: child, activeChatJid });
      }
      return;
   }

   const currentUser = getLocalUserId();
   if (message.type !== 'message') return;
   if (!SEEN_ELIGIBLE_TYPES.has(message.msgType)) return;
   if (getMessageSenderId({ type: 'chats', message }) === currentUser) return;
   if (message.msgStatus === MESSAGE_SEEN_STATUS_ID) return;

   const groupId = isGroupChat(activeChatJid) ? getUserIdFromJid(activeChatJid) : '';
   const toJid = isGroupChat(activeChatJid) ? formatUserIdToJid(message.publisherId) : activeChatJid;

   enqueueSeenStatus({ msgId: message.msgId, publishedAt: message.timestamp, toJid, groupId });
};

// ─── Find nearest scrollable ancestor ───────────────────────────────────────
// Used as IntersectionObserver root so visibility is relative to the chat
// container, not the window viewport.

const findScrollContainer = el => {
   let node = el?.parentElement;
   while (node && node !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(node);
      if (/scroll|auto/.test(overflow) || /scroll|auto/.test(overflowY)) return node;
      node = node.parentElement;
   }
   return null;
};

// ─── Hook ────────────────────────────────────────────────────────────────────
//
// Race condition handled:
//   The IntersectionObserver callback and the isReady effect can fire in any
//   order. Both paths write to shared refs and call the same tryProcessSeen()
//   so whichever arrives last will complete the send — with no double-send.
//
//   Timeline A — element visible first, isReady arrives later:
//     IO fires  → isInViewport=true,  isReady=false → tryProcessSeen: skip
//     effect    → isReady=true                      → tryProcessSeen: SEND ✓
//
//   Timeline B — isReady first, element visible later:
//     effect    → isReady=true,  isInViewport=false → tryProcessSeen: skip
//     IO fires  → isInViewport=true                 → tryProcessSeen: SEND ✓
//
//   Timeline C — both true simultaneously (normal user scroll):
//     IO fires  → isInViewport=true, isReady=true   → tryProcessSeen: SEND ✓
//

export const useSendSeen = ({ message, activeChatJid, isReady, elementRef }) => {
   const isOnline = useIsOnline();
   const { isAppActive } = useAppActivity(isOnline);
   const { canChat } = useChatRequest(activeChatJid);

   const seenRef = useRef({ isInViewport: false, isReady: false, isAppActive: false, canChat: false });

   const dataRef = useRef({ message, activeChatJid });

   useEffect(() => {
      dataRef.current = { message, activeChatJid };
   });

   const tryProcessSeen = () => {
      const s = seenRef.current;
      if (!s.isInViewport) return;
      if (!s.isReady) return;
      if (!s.isAppActive) return;
      if (!s.canChat) return;
      processSeen(dataRef.current);
   };

   useEffect(() => {
      const el = elementRef?.current;
      if (!el) return;
      const root = findScrollContainer(el);

      const observer = new IntersectionObserver(
         ([entry]) => {
            seenRef.current.isInViewport = entry.isIntersecting;
            if (entry.isIntersecting) tryProcessSeen();
         },
         { root, threshold: 0.1 },
      );

      observer.observe(el);
      return () => observer.disconnect();
   }, [elementRef, message.msgId, message.msgStatus, message.editedStatus, activeChatJid]);

   useEffect(() => {
      seenRef.current.isReady = Boolean(isReady);
      seenRef.current.isAppActive = isAppActive;
      seenRef.current.canChat = canChat;
      tryProcessSeen();
   }, [isReady, isAppActive, canChat, message.msgStatus, message.editedStatus]);
};

// ─── Bulk sender for jump-to-bottom ──────────────────────────────────────────

export const sendSeenForMessages = ({ messages, activeChatJid }) => {
   for (const message of messages) {
      processSeen({ message, activeChatJid });
   }

   clearTimeout(flushTimer);
   flushSeenQueue();
};
